import { Card, Stack, Tabs } from "@humaniti/ui";
import { MODALITIES } from "../content/taskTypes";

/**
 * Tabbed presentation of all 17 task types, grouped by modality. Used on
 * ForBuildersPage + ForHumansPage's "Cover every modality" section. Renders:
 *
 *   [ Vision (4) ] [ Language (7) ] [ Audio (2) ] [ Video (2) ] [ 3D & Document (2) ]
 *   ---------------------------------------------------------------------------
 *   <modality intro paragraph>
 *
 *   ┌────────────────────────────┐  ┌────────────────────────────┐
 *   │ Image bbox                 │  │ Polygon                    │
 *   │ Tight rectangles around …  │  │ Multi-vertex outlines for…│
 *   │                            │  │                            │
 *   │ COCO · YOLO · Pascal VOC   │  │ COCO · GeoJSON             │
 *   └────────────────────────────┘  └────────────────────────────┘
 *
 * Shared so the two pages cannot drift in their coverage story; if a task type
 * is added in src/content/taskTypes.ts, both pages pick it up automatically.
 *
 * Layout notes:
 * - Outer width: 100% (overrides parent Stack's align-items: flex-start, which
 *   otherwise collapses the grid to its minmax floor).
 * - Cards stretch to equal heights via the parent grid's default
 *   align-items: stretch.
 * - Outputs row pushes to card bottom via marginTop: auto.
 */
export const ModalityTabs = () => (
  <div style={{ width: "100%" }}>
    <Tabs
      tabs={MODALITIES.map((m) => ({
        value: m.id,
        label: `${m.label} (${m.taskTypes.length})`,
      }))}
      defaultValue={MODALITIES[0]?.id}
    >
      {(active) => {
        const modality = MODALITIES.find((m) => m.id === active) ?? MODALITIES[0];
        if (!modality) return null;
        return (
          <Stack gap="lg">
            <p
              style={{
                margin: 0,
                fontSize: "var(--fs-body)",
                lineHeight: "var(--lh-body)",
                color: "var(--fg-secondary)",
                maxWidth: 720,
              }}
            >
              {modality.intro}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: "var(--space-4)",
                width: "100%",
              }}
            >
              {modality.taskTypes.map((t) => (
                <Card
                  key={t.name}
                  style={{
                    padding: "var(--space-5)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-3)",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontFamily: "var(--font-sans)",
                      fontSize: "18px",
                      lineHeight: "26px",
                      fontWeight: 500,
                      color: "var(--ink)",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {t.name}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "var(--fs-body)",
                      lineHeight: "var(--lh-body)",
                      color: "var(--fg-secondary)",
                    }}
                  >
                    {t.description}
                  </p>
                  <div
                    style={{
                      marginTop: "auto",
                      display: "flex",
                      gap: "var(--space-3)",
                      flexWrap: "wrap",
                      paddingTop: "var(--space-2)",
                    }}
                  >
                    {t.outputs.map((o, i) => (
                      <span
                        key={o}
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "var(--fs-label)",
                          letterSpacing: "0.06em",
                          color: "var(--fg-muted)",
                        }}
                      >
                        {o}
                        {i < t.outputs.length - 1 && (
                          <span style={{ marginLeft: "var(--space-3)" }}>·</span>
                        )}
                      </span>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </Stack>
        );
      }}
    </Tabs>
  </div>
);
